import React, { createContext, Key, ReactNode, useCallback, useContext, useState } from "react";
import { isEmpty } from "lodash";
import {
  GetReconciliationGroupsApiParams,
  PaginatedApiParams,
  StatementTitleParams
} from "../types/reconciliation";

interface ReconciliationGroupsContextType {
  page: number;
  pageSize: number;
  ordering: string | undefined;
  broker: Key[];
  ecPeriod: Key[];
  unreconciledCredit: Key[];
  unreconciledDebit: Key[];
  reconciliationJobStatus: Key[];
  roreJobStatus: Key[];
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setOrdering: (ordering: string) => void;
  setBroker: (broker: Key[]) => void;
  setEcPeriod: (ecPeriod: Key[]) => void;
  setUnreconciledCredit: (unreconciledCredit: Key[]) => void;
  setUnreconciledDebit: (unreconciledDebit: Key[]) => void;
  setReconciliationJobStatus: (status: Key[]) => void;
  setRoreJobStatus: (status: Key[]) => void;
  getParamsForApiRequest: (defaultFilters?: GetReconciliationGroupsApiParams) => (GetReconciliationGroupsApiParams & PaginatedApiParams);
}

const ReconciliationGroupsTableContext = createContext<ReconciliationGroupsContextType | undefined>(undefined);

export const ReconciliationGroupsTableProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [ordering, setOrdering] = useState<string | undefined>(undefined);
  const [broker, setBroker] = useState<Key[]>([]);
  const [ecPeriod, setEcPeriod] = useState<Key[]>([]);
  const [unreconciledCredit, setUnreconciledCredit] = useState<Key[]>([]);
  const [unreconciledDebit, setUnreconciledDebit] = useState<Key[]>([]);
  const [reconciliationJobStatus, setReconciliationJobStatus] = useState<Key[]>([]);
  const [roreJobStatus, setRoreJobStatus] = useState<Key[]>([]);

  const getParamsForApiRequest = useCallback((defaultFilters?: StatementTitleParams) => {
    return {
      page,
      page_size: pageSize,
      ...(!isEmpty(ordering) && { ordering: ordering }),
      //@ts-ignore
      ...(!isEmpty(broker) && { broker: broker?.map(entry => entry.value).join(",") }),
      ...(!isEmpty(ecPeriod) && {
        ec_period_min: ecPeriod?.at(0) as string,
        ec_period_max: ecPeriod?.at(1) as string
      }),
      ...(!isEmpty(unreconciledCredit) && {
        unreconciled_credit_min: unreconciledCredit?.at(0) as string,
        unreconciled_credit_max: unreconciledCredit?.at(1) as string
      }),
      ...(!isEmpty(unreconciledDebit) && {
        unreconciled_debit_min: unreconciledDebit?.at(0) as string,
        unreconciled_debit_max: unreconciledDebit?.at(1) as string
      }),
      ...(!isEmpty(reconciliationJobStatus) && { reconciliation_job_status: reconciliationJobStatus?.join(",") }),
      ...(!isEmpty(roreJobStatus) && { rore_job_status: roreJobStatus?.join(",") }),
      ...(!isEmpty(defaultFilters) && { ...defaultFilters }),
    };
  }, [broker, ecPeriod, ordering, page, pageSize, reconciliationJobStatus, roreJobStatus, unreconciledCredit, unreconciledDebit]);

  const value: ReconciliationGroupsContextType = {
    page,
    pageSize,
    ordering,
    broker,
    ecPeriod,
    unreconciledCredit,
    unreconciledDebit,
    reconciliationJobStatus,
    roreJobStatus,
    setPage,
    setPageSize,
    setOrdering,
    setBroker,
    setEcPeriod,
    setUnreconciledCredit,
    setUnreconciledDebit,
    setReconciliationJobStatus,
    setRoreJobStatus,
    getParamsForApiRequest
  };

  return (
    <ReconciliationGroupsTableContext.Provider value={value}>
      {children}
    </ReconciliationGroupsTableContext.Provider>
  );
};

export const useReconciliationGroupsTable = (): ReconciliationGroupsContextType => {
  const context = useContext(ReconciliationGroupsTableContext);
  if (context === undefined) {
    throw new Error("useReconciliationGroupsTableProvider deve essere utilizzato all'interno di un ReconciliationGroupsTableProvider");
  }
  return context;
};